import { type RefObject, useLayoutEffect, useState } from 'react'

// Horizontal insets for a header's overlay title [data-hdr-title]: centred on the
// screen (mirroring the wider [data-hdr-side] cluster) when it fits, else centred in
// the free space between the clusters so it truncates before the buttons.
export function useTitleInset(headerRef: RefObject<HTMLElement | null>, active: boolean) {
  const [inset, setInset] = useState({ left: 0, right: 0 })
  useLayoutEffect(() => {
    const header = headerRef.current
    if (!header || !active) return
    const sides = [...header.querySelectorAll<HTMLElement>('[data-hdr-side]')]
    const measure = () => {
      const h = header.getBoundingClientRect()
      const mid = h.left + h.width / 2
      let left = 0
      let right = 0
      for (const el of sides) {
        // Slots can be wider than their content (flex-1) or narrower (fixed width
        // with overflowing actions), so measure what's inside them.
        for (const c of el.children.length ? el.children : [el]) {
          const r = c.getBoundingClientRect()
          if (!r.width) continue
          if (r.left + r.width / 2 < mid) left = Math.max(left, r.right - h.left)
          else right = Math.max(right, h.right - r.left)
        }
      }
      // Centre on the screen when the title fits between the wider side and its mirror;
      // only a title too long for that uses the free space off-centre.
      const wide = Math.max(left, right)
      const title = header.querySelector<HTMLElement>('[data-hdr-title]')
      const need = title
        ? Math.max(0, ...[...title.children].map((c) => (c as HTMLElement).scrollWidth))
        : 0
      const fits = h.width - 2 * (wide + 8) >= need
      setInset(
        fits
          ? { left: Math.ceil(wide) + 8, right: Math.ceil(wide) + 8 }
          : { left: Math.ceil(left) + 8, right: Math.ceil(right) + 8 },
      )
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(header)
    for (const el of sides) {
      ro.observe(el)
      for (const c of el.children) ro.observe(c)
    }
    return () => ro.disconnect()
  }, [headerRef, active])
  return inset
}
